import { UtrvType } from '@constants/status';
import { Calculation, CalculationType, GridDashboardChartItem, HistoricalUtrs, SurveyUnitConfig } from '@g17eco/types/insight-custom-dashboard';
import { getNumericValue, getValueWithFallback, sortUtrvByDate } from '@utils/charts';
import { DATE, formatDateUTC } from '@utils/date';
import { FormulaVariables, tryCalculation } from '@utils/formula';
import { getMetricUnitDesc } from '@features/utr-modal/components/chart';
import { isNaOrNr } from '@utils/universalTrackerValue';
import { isNumeric } from '@utils/number';
import { HistoricalGenerated } from '@g17eco/types/app-integration';
import { UniversalTrackerModalServiceUtrv } from '@g17eco/types/universalTrackerValue';
import { BaseHistoricalUtr, ChartDataResponse, DataProps } from '../../types';
import { getCurrencySymbol, UnitTypes } from '@utils/units';
import { isPercentageValueType } from '@utils/universalTracker';

// The body operates on `BaseHistoricalUtr['utrvs']` — only the Pick fields it reads.
// Overloads let callers passing the wider `HistoricalUtrs[]` keep their full utrv shape
// (they need `utrv.initiative` etc. downstream); narrow / union callers fall through to
// the BaseHistoricalUtr signature. This is the canonical TS workaround for design
// limitations around generic-body indexed access (TS issue #40832) and union-input
// inference (TS issue #52295) — no internal `as T['utrvs']` casts, no `as unknown as` at
// call sites; the runtime invariant ("filter/sort preserve element type") is encoded in
// the overload pair rather than a body cast.

export function getActualUtrvs(utrsData: HistoricalUtrs[]): HistoricalUtrs['utrvs'];
export function getActualUtrvs(utrsData: BaseHistoricalUtr[]): BaseHistoricalUtr['utrvs'];
export function getActualUtrvs(utrsData: BaseHistoricalUtr[]): BaseHistoricalUtr['utrvs'] {
  return utrsData.reduce<BaseHistoricalUtr['utrvs']>((acc, cur) => {
    return [...acc, ...cur.utrvs.filter((utrv) => utrv.type === UtrvType.Actual)];
  }, []);
}

export function getActualUtrvsGroupByDate(utrsData: HistoricalUtrs[]): { [key: string]: HistoricalUtrs['utrvs'] };
export function getActualUtrvsGroupByDate(utrsData: BaseHistoricalUtr[]): { [key: string]: BaseHistoricalUtr['utrvs'] };
export function getActualUtrvsGroupByDate(utrsData: BaseHistoricalUtr[]): { [key: string]: BaseHistoricalUtr['utrvs'] } {
  const utrvs = getActualUtrvs(utrsData).slice().sort(sortUtrvByDate);

  return utrvs.reduce<{ [key: string]: BaseHistoricalUtr['utrvs'] }>((utrvsGroupByDate, utrv) => {
    const date = formatDateUTC(utrv.effectiveDate, DATE.MONTH_YEAR_SHORT);
    utrvsGroupByDate[date] = [...(utrvsGroupByDate[date] ?? []), utrv];
    return utrvsGroupByDate;
  }, {});
}

type LatestActualUtrvs<U> = {
  latestActualUtrvs: U[];
  effectiveDate: string;
  period: BaseHistoricalUtr['utrvs'][number]['period'];
  aggregated: boolean;
};

export function getLatestActualUtrvs(
  utrsData: HistoricalUtrs[]
): LatestActualUtrvs<HistoricalUtrs['utrvs'][number]> | Record<string, never>;
export function getLatestActualUtrvs(
  utrsData?: BaseHistoricalUtr[]
): LatestActualUtrvs<BaseHistoricalUtr['utrvs'][number]> | Record<string, never>;
export function getLatestActualUtrvs(
  utrsData?: BaseHistoricalUtr[]
): LatestActualUtrvs<BaseHistoricalUtr['utrvs'][number]> | Record<string, never> {
  if (!utrsData?.length) {
    return {};
  }

  const sortedUtrvs = getActualUtrvs(utrsData).slice().sort(sortUtrvByDate).reverse();
  const latestActual = sortedUtrvs[0];

  if (!latestActual) {
    return {};
  }

  const { effectiveDate, period } = latestActual;

  const latestActualUtrvs = sortedUtrvs.filter(
    (utrv) => utrv.effectiveDate === effectiveDate && utrv.period === period
  );

  return {
    latestActualUtrvs,
    effectiveDate,
    period,
    aggregated: latestActualUtrvs.some((utrv) => utrv.aggregationCount),
  };
}

export const getVariablesWithValues = ({
  utrsData,
  variables,
  utrvs,
  fallback = 0,
}: Required<Pick<GridDashboardChartItem, 'variables'>> & {
  utrsData: BaseHistoricalUtr[];
  utrvs: BaseHistoricalUtr['utrvs'];
  fallback?: number | string | undefined;
}) => {
  return Object.keys(variables).reduce((variablesWithValues, variable) => {
    variablesWithValues[variable] = fallback;

    const utrData = utrsData.find((utrData) => utrData.utr.code === variables[variable].code);
    if (!utrData) {
      return variablesWithValues;
    }

    const utrv = utrvs.find((utrv) => utrv.universalTrackerId === utrData.utr._id);
    if (!utrv || isNaOrNr(utrv)) {
      return variablesWithValues;
    }

    if (isNumeric(fallback)) {
      // fallback is a number, therefore the value must be numeric
      variablesWithValues[variable] = getNumericValue(utrv, variables[variable].valueListCode, { isMultiRowsTable: true });
      return variablesWithValues;
    }

    variablesWithValues[variable] = getValueWithFallback(utrv, variables[variable].valueListCode, { isMultiRowsTable: true, fallback });
    return variablesWithValues;
  }, {} as FormulaVariables);
};

export const getMetricUnit = (utrsData: Pick<HistoricalGenerated, 'utr'>[]) => {
  return utrsData?.[0]?.utr ? getMetricUnitDesc(utrsData[0].utr, undefined, '') : '';
};

export const getSumOfVariables = (variablesWithValues: FormulaVariables) =>
  Object.values(variablesWithValues).reduce((acc, cur) => Number(acc) + Number(cur), 0) as number;

export const getLatestActualAndTargetSingleUtr = ({
  utrsData,
  calculation,
  variables,
}: { utrsData: BaseHistoricalUtr[] } & Required<Pick<GridDashboardChartItem, 'calculation' | 'variables'>>) => {
  const [variable] = Object.values(variables);
  const utrData = utrsData.find((data) => data.utr.code === (variable ? variable.code : ''));

  if (!utrData) {
    return {
      variablesWithValues: {},
    };
  }

  const sortedUtrvs = utrData.utrvs.slice().sort(sortUtrvByDate).reverse();
  const latestActualUtrv = sortedUtrvs.find((utrv) => utrv.type === UtrvType.Actual);
  const latestTargetUtrv = sortedUtrvs.find((utrv) => utrv.type === UtrvType.Target);

  const variablesWithActualValue = getVariablesWithValues({
    utrsData,
    variables,
    utrvs: latestActualUtrv ? [latestActualUtrv] : [],
    fallback: '',
  });

  const actualValue = tryCalculation({
    formula: calculation.values[0].formula || '',
    variables: variablesWithActualValue,
    fallback: '',
    infinityFallback: '',
  });

  const variablesWithTargetValue = getVariablesWithValues({
    utrsData,
    variables,
    utrvs: latestTargetUtrv ? [latestTargetUtrv] : [],
    fallback: '',
  });

  const targetValue = tryCalculation({
    formula: calculation.values[0].formula || '',
    variables: variablesWithTargetValue,
    fallback: '',
    infinityFallback: '',
  });
  const effectiveDate = latestActualUtrv?.effectiveDate || latestTargetUtrv?.effectiveDate || '';
  const period = latestActualUtrv?.period || latestTargetUtrv?.period || '';
  // Caption flag tracks the displayed value's source — only the actual drives it.
  const aggregated = Boolean(latestActualUtrv?.aggregationCount);

  return { actualValue, targetValue, effectiveDate, period, aggregated, variablesWithValues: variablesWithActualValue, latestActualUtrv };
};

const getTextColorWithoutDirection = ({
  actualValue,
  targetValue,
}: {
  actualValue: number | string | undefined;
  targetValue: number | string | undefined;
}) => {
  if (Number(actualValue) > Number(targetValue)) {
    return 'text-ThemeSuccessMedium';
  }
  return 'text-ThemeWarningMedium';
};

const getTextColorWithDirection = ({
  actualValue,
  targetValue,
  targetDirection,
}: {
  actualValue: number | string | undefined;
  targetValue: number | string | undefined;
  targetDirection: string;
}) => {
  if (targetDirection === 'decrease') {
    return Number(actualValue) < Number(targetValue) ? 'text-ThemeSuccessMedium' : 'text-ThemeWarningMedium';
  }
  if (targetDirection === 'increase') {
    return Number(actualValue) > Number(targetValue) ? 'text-ThemeSuccessMedium' : 'text-ThemeWarningMedium';
  }
  // unsupported target direction, return default color
  return 'text-ThemeTextMedium';
};

export const getTextColor = ({
  actualValue,
  targetValue,
  targetDirection,
}: {
  actualValue: number | string | undefined;
  targetValue: number | string | undefined;
  targetDirection?: string;
}) => {
  if (!actualValue || !targetValue) {
    return 'text-ThemeTextMedium';
  }
  if (!targetDirection) {
    return getTextColorWithoutDirection({ actualValue, targetValue });
  }
  return getTextColorWithDirection({ actualValue, targetValue, targetDirection });
};

interface VAxisTitle {
  unitText: string | undefined;
  calculation: Calculation;
  utrsData: BaseHistoricalUtr[];
}

export const getVAxisTitle = ({ unitText, calculation, utrsData }: VAxisTitle) => {
  // Truthy: persisted `unitText: ''` must hit the same fallback as undefined. Callers
  // needing to suppress the fallback use the `vAxisTitle` override on the chart instead.
  if (unitText) {
    return unitText;
  }

  return calculation.type === CalculationType.Percentage ? '%' : getMetricUnit(utrsData);
};

/**
 * Gets the Google Charts vAxis format string for currency values.
 * Example: "$#,###" for USD, "€#,###" for EUR
 */
export const getCurrencyAxisFormat = (currencySymbol: string | undefined): string | undefined => {
  if (!currencySymbol) {
    return undefined;
  }
  // Use currency symbol as prefix in format pattern
  // This will format values like: $1,234 or €1,234
  return `${currencySymbol}#,###`;
};

/**
 * Determines if a currency symbol should be displayed as a prefix or suffix.
 * Proper symbols (like $, €, £, ¥, A$, C$) should be prefix: "$100"
 * Code-based symbols (like CHF, AED, SAR) should be suffix: "100 CHF"
 *
 * Heuristic: If the symbol is 3+ uppercase letters (ISO currency code format), use suffix.
 * Otherwise use prefix (handles symbols like $, €, £, ¥, A$, HK$, E£, Rp, RM, KSh).
 */
export const isCurrencyPrefix = (currencySymbol: string | undefined): boolean => {
  if (!currencySymbol) {
    return false;
  }
  // ISO currency codes are exactly 3 uppercase letters
  // If the symbol matches this pattern, it's a code (use as suffix)
  // Otherwise it's a proper symbol (use as prefix)
  const isIsoCode = /^[A-Z]{3}$/.test(currencySymbol);
  return !isIsoCode;
};

interface GetCurrencyConfigParams {
  utrsData: HistoricalUtrs[];
  surveyUnitConfigMap: Map<string, SurveyUnitConfig>;
  variables: Record<string, { code: string; valueListCode?: string }>;
}

/** Minimal UTR data type needed for currency type checking */
type UtrDataForCurrencyCheck = {
  utr: {
    code: string;
    unitType?: string;
    valueValidation?: {
      table?: {
        columns?: Array<{ code: string; unitType?: string }>;
      };
    };
  };
};

/**
 * Checks if a variable represents a currency type based on UTR/column metadata.
 * If variable has valueListCode, it's a table column - check column's unitType.
 * Otherwise, check the UTR's unitType directly.
 */
export const isVariableCurrencyType = (
  variable: { code: string; valueListCode?: string },
  utrsData: UtrDataForCurrencyCheck[]
): boolean => {
  const utrData = utrsData.find((data) => data.utr.code === variable.code);
  if (!utrData) {
    return false;
  }

  if (variable.valueListCode) {
    const column = utrData.utr.valueValidation?.table?.columns?.find(
      (col) => col.code === variable.valueListCode
    );
    return column?.unitType === UnitTypes.currency;
  }

  return utrData.utr.unitType === UnitTypes.currency;
};

/**
 * Determines if currency formatting should be applied to a calculated value.
 *
 * This function checks whether ALL variables in a formula are currency type.
 * Currency prefix is only shown when all variables represent currency values,
 * ensuring that mixed calculations (e.g., revenue / headcount = $/person)
 * don't incorrectly display as pure currency amounts.
 *
 * @example
 * // revenue - expenses (both currency) → shows $600
 * areAllVariablesCurrencyType({ a: { code: 'revenue' }, b: { code: 'expenses' } }, utrsData) // true
 *
 * @example
 * // revenue / headcount (currency / count) → shows 100 (not $100)
 * areAllVariablesCurrencyType({ a: { code: 'revenue' }, b: { code: 'headcount' } }, utrsData) // false
 *
 * @param variables - Record of variable definitions with UTR codes and optional valueListCode for table columns
 * @param utrsData - Array of UTR data containing unitType information
 * @returns true if ALL variables are currency type; false if empty, mixed types, or any non-currency
 */
export const areAllVariablesCurrencyType = (
  variables: Record<string, { code: string; valueListCode?: string }> | undefined,
  utrsData: UtrDataForCurrencyCheck[]
): boolean => {
  if (!variables) {
    return false;
  }

  const variableValues = Object.values(variables);
  if (variableValues.length === 0) {
    return false;
  }

  return variableValues.every((variable) => isVariableCurrencyType(variable, utrsData));
};

/**
 * Collects currencies from UTRVs based on their associated survey's unitConfig.
 * Works for both direct UTRVs and aggregated UTRVs with disaggregation.
 */
const collectCurrenciesFromUtrvs = (
  utrsData: HistoricalUtrs[],
  variables: Record<string, { code: string; valueListCode?: string }>,
  surveyUnitConfigMap: Map<string, SurveyUnitConfig>
): Set<string> => {
  const currencies = new Set<string>();

  for (const variable of Object.values(variables)) {
    const utrData = utrsData.find((data) => data.utr.code === variable.code);
    if (!utrData) {
      continue;
    }

    for (const utrv of utrData.utrvs) {
      // Check direct surveyId on UTRV
      const surveyId = utrv.compositeData?.surveyId;
      if (surveyId) {
        const unitConfig = surveyUnitConfigMap.get(surveyId)?.unitConfig;
        if (unitConfig?.currency) {
          currencies.add(unitConfig.currency);
        }
      }

      // Also check disaggregation array for aggregated UTRVs
      // The disaggregation contains source UTRVs with their own compositeData
      if (utrv.disaggregation) {
        for (const disaggUtrv of utrv.disaggregation) {
          const disaggSurveyId = (disaggUtrv as { compositeData?: { surveyId?: string } }).compositeData?.surveyId;
          if (disaggSurveyId) {
            const unitConfig = surveyUnitConfigMap.get(disaggSurveyId)?.unitConfig;
            if (unitConfig?.currency) {
              currencies.add(unitConfig.currency);
            }
          }
        }
      }
    }
  }

  return currencies;
};

export type CurrencyConfig = {
  currencySymbol?: string;
  currencyCode?: string;
  /** Fallback for looking up currency per-UTRV when there's no consistent currency across all UTRVs */
  surveyUnitConfigMap: Map<string, SurveyUnitConfig>;
}

export const EMPTY_CURRENCY_CONFIG: CurrencyConfig = { surveyUnitConfigMap: new Map() };

export const getCurrencyConfig = ({
  utrsData,
  surveyUnitConfigMap,
  variables,
}: GetCurrencyConfigParams): CurrencyConfig => {
  if (!utrsData.length || !Object.keys(variables).length) {
    return { surveyUnitConfigMap };
  }

  // Collect currencies from all UTRVs
  const currencies = collectCurrenciesFromUtrvs(utrsData, variables, surveyUnitConfigMap);

  // If no consistent currency, return just the map for per-UTRV lookup
  if (currencies.size !== 1) {
    return { surveyUnitConfigMap };
  }

  const [currency] = currencies;
  return {
    currencySymbol: getCurrencySymbol(currency),
    currencyCode: currency,
    surveyUnitConfigMap,
  };
};

/**
 * Gets the currency symbol for a specific UTRV from surveyUnitConfigMap.
 * Used as fallback when there's no consistent currency across all UTRVs.
 */
export const getCurrencyForUtrv = (
  utrv: { compositeData?: { surveyId?: string } } | undefined,
  surveyUnitConfigMap: Map<string, SurveyUnitConfig> | undefined
): { currencySymbol: string; currencyCode: string } | undefined => {
  if (!utrv || !surveyUnitConfigMap) {
    return undefined;
  }

  const surveyId = utrv.compositeData?.surveyId;
  if (!surveyId) {
    return undefined;
  }

  const currency = surveyUnitConfigMap.get(surveyId)?.unitConfig?.currency;
  if (!currency) {
    return undefined;
  }

  return {
    currencySymbol: getCurrencySymbol(currency),
    currencyCode: currency,
  };
};

/**
 * Gets the currency for the latest actual UTRV from a list of UTRVs.
 * Used for single-value widgets when there's no consistent currency across all UTRVs.
 */
export const getLatestActualUtrvCurrency = (
  utrvs: Pick<UniversalTrackerModalServiceUtrv, 'type' | 'effectiveDate' | 'compositeData'>[] | undefined,
  surveyUnitConfigMap: Map<string, SurveyUnitConfig> | undefined
): { currencySymbol: string; currencyCode: string } | undefined => {
  if (!utrvs?.length) {
    return undefined;
  }
  const latestActualUtrv = utrvs
    .filter((utrv) => utrv.type === UtrvType.Actual)
    .sort(sortUtrvByDate)
    .reverse()[0];
  return getCurrencyForUtrv(latestActualUtrv, surveyUnitConfigMap);
};

/**
 * Checks if chart data has actual data rows beyond the header row.
 * Chart data format: [header, ...dataRows] where header is the first row.
 *
 * Google Charts will fail with "Data column(s) for axis #0 cannot be of type string"
 * if data only contains headers without actual data rows.
 *
 * @param chartData - The chart data array where first row is header
 * @returns true if chartData has at least one data row beyond the header
 */
export const hasChartDataRows = (chartData: unknown[][] | undefined): boolean => {
  return !!chartData && chartData.length > 1;
};

export const hasNegativeChartValues = (chartData: unknown[][] | undefined): boolean => {
  if (!chartData || chartData.length <= 1) {
    return false;
  }

  // Skip header row and check data rows for any negative numbers
  return chartData.slice(1).some((row) => row.some((value) => typeof value === 'number' && value < 0));
};

// Add remainder slice for partial pie/donut chart filling when:
// 1. Only one variable is selected & the UTR valueType is percentage
// 2. The value is less than the max (to show unfilled portion)
export const getUnfilledPortion = <T extends BaseHistoricalUtr>({
  variables,
  utrsData,
  chartData,
}: Pick<DataProps<T>, 'variables' | 'utrsData'> & { chartData: ChartDataResponse['chartData'] }) => {
  if (Object.keys(variables).length === 1 && utrsData.length > 0 && chartData.length === 2) {
    const firstUtr = utrsData[0].utr;
    const isPercentage = isPercentageValueType(firstUtr.valueType);
    if (!isPercentage || (firstUtr.valueValidation?.min ?? 0) < 0) {
      return;
    }
    const [_headerRow, dataRow] = chartData;
    const [_name, value] = dataRow;
    const maxValue = firstUtr.valueValidation?.max ?? 100;

    if (typeof value === 'number' && value >= 0 && value < maxValue) {
      return ['Remaining', maxValue - value];
    }
  }

  return;
};
